import { useMemo } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, ReferenceLine, Tooltip, CartesianGrid,
} from 'recharts';
import { type CallEmotions, type TimelinePoint, emotionMeta } from './types';

const AGENT_COLOR = '#2563eb';    // כחול
const CUSTOMER_COLOR = '#16a34a'; // ירוק

const fmtTime = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
};

/**
 * גרף רגשות כרונולוגי: שני קווים (נציג כחול / לקוח ירוק) של valence לאורך
 * ציר הזמן, עם אמוג'י רגש בנקודות מפתח. לחיצה על נקודה קופצת לזמן באודיו.
 */
export function EmotionTimeline({ emotions, onSeek }: {
  emotions: CallEmotions; onSeek?: (t: number) => void;
}) {
  // ממזגים את שני הדוברים לרשת זמן אחת (recharts מצייר null כפער).
  const { data, keyMoments } = useMemo(() => {
    const times = Array.from(new Set(emotions.timeline.map((p) => Math.round(p.t)))).sort((a, b) => a - b);
    const at = (arr: TimelinePoint[], sp: string, t: number) => {
      const p = arr.find((x) => Math.round(x.t) === t && x.speaker === sp);
      return p ? p.valence : null;
    };
    const rows = times.map((t) => ({
      t,
      agent: at(emotions.timeline, 'agent', t),
      customer: at(emotions.timeline, 'customer', t),
    }));
    return { data: rows, keyMoments: emotions.key_moments ?? [] };
  }, [emotions]);

  const onDotClick = (payload: unknown) => {
    const t = (payload as { payload?: { t?: number } })?.payload?.t;
    if (typeof t === 'number') onSeek?.(t);
  };

  if (data.length === 0) return null;

  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: '12px 8px', background: 'var(--surface)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 8px 8px' }}>
        <strong style={{ fontSize: 'var(--t-sm)' }}>מסע רגשי לאורך השיחה</strong>
        <div style={{ display: 'flex', gap: 12, fontSize: 'var(--t-xs)' }}>
          <span style={{ color: AGENT_COLOR }}>● נציג</span>
          <span style={{ color: CUSTOMER_COLOR }}>● לקוח</span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={data} margin={{ top: 24, right: 12, left: 4, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" vertical={false} />
          <XAxis dataKey="t" tickFormatter={fmtTime} tick={{ fontSize: 11 }} stroke="var(--muted)" />
          <YAxis domain={[-1, 1]} ticks={[-1, 0, 1]} tick={{ fontSize: 11 }} stroke="var(--muted)"
            tickFormatter={(v) => (v > 0 ? 'חיובי' : v < 0 ? 'שלילי' : '0')} width={44} />
          <ReferenceLine y={0} stroke="var(--muted)" strokeDasharray="2 2" />
          <Tooltip
            labelFormatter={(t) => `זמן ${fmtTime(Number(t))}`}
            formatter={((v: unknown, name: unknown) => [Number(v).toFixed(2), name === 'agent' ? 'נציג' : 'לקוח']) as never}
            contentStyle={{ fontSize: 12, borderRadius: 8, direction: 'rtl' }}
          />
          <Line type="monotone" dataKey="agent" stroke={AGENT_COLOR} strokeWidth={2} connectNulls dot={{ r: 3 }} activeDot={{ r: 5, onClick: onDotClick } as never} isAnimationActive={false} />
          <Line type="monotone" dataKey="customer" stroke={CUSTOMER_COLOR} strokeWidth={2} connectNulls dot={{ r: 3 }} activeDot={{ r: 5, onClick: onDotClick } as never} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>

      {/* אמוג'י רגש בנקודות מפתח — לחיצה קופצת לזמן */}
      {keyMoments.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '4px 8px 0' }}>
          {keyMoments.map((k, i) => {
            const m = emotionMeta(k.emotion);
            return (
              <button key={i} type="button" onClick={() => onSeek?.(k.t)} title={k.phrase}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 'var(--t-xs)', padding: '2px 8px', borderRadius: 999, background: m.color + '18', color: m.color, border: `1px solid ${m.color}44`, cursor: 'pointer' }}>
                <span>{m.emoji}</span>
                <span style={{ color: 'var(--muted)' }}>{fmtTime(k.t)}</span>
                <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>"{k.phrase}"</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
